import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { usePartnerSettings } from '@/hooks/usePartnerSettings';
import { AlertCircle, RotateCcw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const DEFAULT_WEIGHTS = {
  safety_weight: 40,
  quality_weight: 40,
  reliability_weight: 20,
};

export function RatingWeightsSettings() {
  const { settings, isLoading, updateSettings } = usePartnerSettings();
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings?.rating_weights) {
      // Convert from decimal (0.40) to percentage (40) for UI display
      setWeights({
        safety_weight: (settings.rating_weights.safety_weight || 0.40) * 100,
        quality_weight: (settings.rating_weights.quality_weight || 0.40) * 100,
        reliability_weight: (settings.rating_weights.reliability_weight || 0.20) * 100,
      });
    }
  }, [settings]);

  const total = weights.safety_weight + weights.quality_weight + weights.reliability_weight;
  const isValid = total === 100;

  const handleSave = async () => {
    if (!isValid) return;
    // Convert from percentage (40) to decimal (0.40) for database storage
    await updateSettings({ 
      rating_weights: {
        safety_weight: weights.safety_weight / 100,
        quality_weight: weights.quality_weight / 100,
        reliability_weight: weights.reliability_weight / 100,
      }
    });
    setHasChanges(false);
  };

  const handleReset = () => {
    setWeights(DEFAULT_WEIGHTS);
    setHasChanges(true);
  };

  const handleWeightChange = (key: keyof typeof weights, value: number[]) => {
    setWeights({ ...weights, [key]: value[0] });
    setHasChanges(true);
  };

  // Example calculation
  const exampleSafety = 4.5;
  const exampleQuality = 3.8;
  const exampleReliability = 4.2;
  const exampleOverall = (
    (exampleSafety * weights.safety_weight / 100) +
    (exampleQuality * weights.quality_weight / 100) +
    (exampleReliability * weights.reliability_weight / 100)
  ).toFixed(2);

  if (isLoading) {
    return <Card><CardContent className="py-6">Loading...</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rating Weights</CardTitle>
        <CardDescription>
          Configure how overall ratings are calculated from individual performance metrics
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Safety Rating Weight</Label>
              <span className="text-sm font-medium">{weights.safety_weight}%</span>
            </div>
            <Slider
              value={[weights.safety_weight]}
              onValueChange={(value) => handleWeightChange('safety_weight', value)}
              max={100}
              step={5}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Quality Rating Weight</Label>
              <span className="text-sm font-medium">{weights.quality_weight}%</span>
            </div>
            <Slider
              value={[weights.quality_weight]}
              onValueChange={(value) => handleWeightChange('quality_weight', value)}
              max={100}
              step={5}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Reliability Rating Weight</Label>
              <span className="text-sm font-medium">{weights.reliability_weight}%</span>
            </div>
            <Slider
              value={[weights.reliability_weight]}
              onValueChange={(value) => handleWeightChange('reliability_weight', value)}
              max={100}
              step={5}
              className="w-full"
            />
          </div>
        </div>

        <div className="rounded-lg border p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Total Weight:</span>
            <span className={`font-bold ${isValid ? 'text-green-600' : 'text-destructive'}`}>
              {total}%
            </span>
          </div>
          {!isValid && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Total must equal 100%. Current total: {total}%
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="rounded-lg bg-muted p-4 space-y-2">
          <div className="text-sm font-medium">Formula Preview:</div>
          <div className="text-sm text-muted-foreground font-mono">
            Overall = ({exampleSafety} × {(weights.safety_weight / 100).toFixed(2)}) + 
            ({exampleQuality} × {(weights.quality_weight / 100).toFixed(2)}) + 
            ({exampleReliability} × {(weights.reliability_weight / 100).toFixed(2)}) = {exampleOverall} ★
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={handleSave} 
            disabled={!isValid || !hasChanges}
          >
            Save Changes
          </Button>
          <Button 
            variant="outline" 
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
